import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, ExternalLink, Users, Image as ImageIcon, Eye, ClipboardCheck } from 'lucide-react';
import { adminAudit, adminBatches, adminValidateBatch } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const VAL = { valide: ['Validé', 'bg-green-600'], a_verifier: ['À vérifier', 'bg-amber-500'], en_attente: ['En attente', 'bg-slate-500'], masque: ['Masqué', 'bg-red-600'] };

const AuditPanel = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setData(await adminAudit());
    const b = await adminBatches(); setBatches(b.batches || []);
  };
  useEffect(() => { load().catch(() => setData({ counts: {}, items: [] })); }, []);

  const items = useMemo(() => {
    if (!data) return [];
    let list = data.items;
    if (filter !== 'all') list = list.filter((x) => x.validation === filter);
    if (q.trim()) { const s = q.toLowerCase(); list = list.filter((x) => (x.titre || '').toLowerCase().includes(s)); }
    return list;
  }, [data, filter, q]);

  const toggle = (sid) => setSelected((p) => ({ ...p, [sid]: !p[sid] }));
  const selectBatch = (idx) => {
    const next = {}; (batches[idx] || []).forEach((c) => { next[c.source_id] = true; }); setSelected(next);
    toast({ title: `Lot ${idx + 1} sélectionné`, description: `${(batches[idx] || []).length} comics.` });
  };
  const validate = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]).map(Number);
    if (ids.length === 0) { toast({ title: 'Aucune sélection' }); return; }
    if (!window.confirm(`Rendre PUBLICS ${ids.length} comics ? (les « à vérifier »/« masqués » seront ignorés)`)) return;
    setBusy(true);
    try { const r = await adminValidateBatch(ids); setReport(r); setSelected({}); await load(); toast({ title: 'Lot validé', description: `${r.valides} publié(s), ${r.ignores} ignoré(s).` }); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const c = data.counts || {};
  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[['total', 'Audités'], ['valide', 'Validés'], ['en_attente', 'En attente'], ['a_verifier', 'À vérifier'], ['masque', 'Masqués']].map(([k, l]) => (
          <div key={k} className="bg-card border border-border rounded-xl p-3 text-center"><p className="text-2xl font-800 font-display">{c[k] ?? 0}</p><p className="text-xs font-700 text-muted-foreground">{l}</p></div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-4">
        <p className="font-800 text-sm mb-2 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-primary" /> Validation accélérée par lots (20)</p>
        <div className="flex flex-wrap gap-2 items-center">
          {batches.map((b, i) => <button key={i} onClick={() => selectBatch(i)} className="px-3 py-1.5 rounded-full bg-muted font-800 text-xs hover:bg-secondary/40">Lot {i + 1} ({b.length})</button>)}
          <span className="text-xs text-muted-foreground font-600 ml-2">{selCount} sélectionné(s)</span>
          <button onClick={validate} disabled={busy || selCount === 0} className="ml-auto flex items-center gap-2 px-5 py-2 rounded-full bg-green-600 text-white font-800 text-sm disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Valider la sélection</button>
        </div>
        {report && <p className="text-sm font-700 mt-2 text-green-600">Rapport: {report.valides} validé(s), {report.ignores} ignoré(s), {report.errors} erreur(s).</p>}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {[['all', 'Tous'], ['valide', 'Validés'], ['en_attente', 'En attente'], ['a_verifier', 'À vérifier'], ['masque', 'Masqués']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-sm font-800 ${filter === k ? 'bg-primary text-white' : 'bg-muted'}`}>{l}</button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un titre..." className="ml-auto px-4 py-2 rounded-full bg-muted font-600 text-sm outline-none" />
      </div>

      <div className="space-y-2">
        {items.map((e) => (
          <div key={e.source_id} className={`flex gap-3 items-center bg-card border rounded-2xl p-2 ${e.anomalies.length ? 'border-amber-300' : 'border-border'}`}>
            <input type="checkbox" checked={!!selected[e.source_id]} onChange={() => toggle(e.source_id)} disabled={e.validation !== 'en_attente'} className="w-4 h-4 accent-[#ed5c1f] shrink-0" title={e.validation !== 'en_attente' ? 'Seuls les « en attente » sont validables par lot' : ''} />
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted shrink-0">{e.featured_image?.url ? <img src={e.featured_image.url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-muted-foreground" /></div>}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><span className={`px-2 py-0.5 rounded-full text-[10px] font-800 text-white uppercase ${VAL[e.validation]?.[1]}`}>{VAL[e.validation]?.[0]}</span><span className="text-[11px] text-muted-foreground font-700">#{e.source_id}</span></div>
              <p className="font-display font-700 truncate">{e.titre}</p>
              <p className="text-xs text-muted-foreground truncate flex items-center gap-2"><ImageIcon className="w-3 h-3" />{e.images_count} <Users className="w-3 h-3" />{(e.auteurs || []).map((a) => a.name).join(', ') || '—'}</p>
              {e.anomalies.length > 0 && <p className="text-[11px] font-800 text-amber-600 flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3" /> {e.anomalies.join(' · ')}</p>}
            </div>
            <a href={`/toon/${e.slug}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-secondary/40 shrink-0" title="Aperçu lecteur"><Eye className="w-4 h-4" /></a>
            {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-secondary/40 shrink-0" title="Source"><ExternalLink className="w-4 h-4" /></a>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditPanel;
